// Types mirror the FROZEN API contract (MASTER_SPEC §22/§13/§15).
// The backend is same-origin JSON; a session cookie is set by login.

export type Role = 'user' | 'admin';

export type RetentionPolicy = 'KEEP_ALL' | 'KEEP_LATEST' | 'KEEP_LAST_N';

export const RETENTION_POLICIES: RetentionPolicy[] = [
  'KEEP_ALL',
  'KEEP_LATEST',
  'KEEP_LAST_N',
];

export interface Me {
  username: string;
  role: Role;
  tenant: string;
  tenantName: string;
  /** True while this session is an admin's time-boxed "view as tenant" grant. */
  impersonating: boolean;
}

export interface Archive {
  id: string;
  name: string;
  retentionPolicy: RetentionPolicy;
  objects: number;
  bytes: number;
  createdAt: string;
}

export interface ArchiveDetail {
  archive: Archive;
  // Present when the archive was created by S3 import — enables one-click sync.
  source?: { endpoint: string; bucket: string; prefix?: string };
  // The contract allows additional fields; we keep them opaque.
  [key: string]: unknown;
}

/** A background synchronisation of an archive from its S3 source. */
export interface SyncResult {
  jobId: string;
  archiveId: string;
  /** running | completed | completed_partial | failed */
  status: string;
  /** True once the job reached a terminal state; stop polling then. */
  done: boolean;
  /** Denominator of the progress bar: what the source listing announced. */
  totalObjects: number;
  totalBytes: number;
  /** Numerator: what the worker has copied so far. */
  imported: number;
  bytes: number;
  /** Objects the run could not take (each larger than the per-run budget). */
  skipped?: number;
  skippedBytes?: number;
  /** The per-run byte budget in force, so the UI can name the actual figure. */
  limitBytes?: number;
  startedAt?: string;
  startedBy?: string;
  finishedAt?: string;
  error?: string;
}

// A sealed, burned DMD image of an archive at a point in time.
export interface Snapshot {
  id: string;
  seq: number;
  trigger: 'manual' | 'scheduled' | string;
  objectCount: number;
  bytes: number;
  dmdSize: number;
  sha256: string;
  createdBy: string;
  createdAt: string;
}

export type ScheduleFreq = 'off' | 'daily' | 'weekly' | 'monthly';
export const SCHEDULE_FREQS: ScheduleFreq[] = ['off', 'daily', 'weekly', 'monthly'];

// hour/minute are in the service schedule timezone; weekday 1=Mon..7=Sun
// (weekly); dom 1..31 or 0 for "last day of month" (monthly).
export interface SchedulePart {
  freq: ScheduleFreq;
  hour: number;
  minute: number;
  weekday: number;
  dom: number;
}

export interface Schedule {
  syncFreq: ScheduleFreq;
  syncHour: number;
  syncMinute: number;
  syncWeekday: number;
  syncDom: number;
  syncNext?: string | null;
  syncLast?: string | null;
  commitFreq: ScheduleFreq;
  commitHour: number;
  commitMinute: number;
  commitWeekday: number;
  commitDom: number;
  commitNext?: string | null;
  commitLast?: string | null;
}

export interface ArchiveObject {
  id: string;
  path: string;
  bytes: number;
  sha256: string;
  versions: number;
  createdAt: string;
}

export interface SearchHit {
  archiveId: string;
  archiveName: string;
  path: string;
  bytes: number;
}

export interface AuditRecord {
  seq: number;
  occurredAt: string;
  actor: string;
  action: string;
  resource: string;
  result: string;
}

export interface ObjectVersion {
  version: number;
  bytes: number;
  sha256: string;
  createdAt: string;
}

export interface RetrievalEstimate {
  media: number;
  days: number;
  eur: number;
  spanned: boolean;
}

export interface RetrievalObjectLink {
  path: string;
  bytes: number;
  url: string;
}

// Result of a fulfilled retrieval: a dedicated S3 bucket populated with the
// requested objects plus time-limited presigned download links.
export interface RetrievalResult {
  bucket: string;
  endpoint: string;
  objectCount: number;
  bytes: number;
  expiresInHours: number;
  objects: RetrievalObjectLink[];
}

export interface BillingRecord {
  period: string;
  mediaWritten: number;
  mediaRetrieved: number;
  eur: number;
}

export interface BillingEstimate {
  records: BillingRecord[];
  totalEUR: number;
  /** Storage cost/month at the plan's per-GB-year rate for currently stored
   * bytes. Absent/0 when the tenant has no subscription. */
  currentStorageEurPerMonth?: number;
  /** The plan this estimate was priced by (single-tenant views only). */
  planName?: string;
  planVersion?: number;
}

export type NotificationKind = 'info' | 'success' | 'warning' | 'error' | string;

export interface Notification {
  id: string;
  at: string;
  kind: NotificationKind;
  message: string;
  read: boolean;
}

export interface S3Source {
  name: string;
  bucket: string;
  endpoint: string;
  prefix?: string;
}

export interface S3ImportRequest {
  bucket: string;
  prefix: string;
  endpoint: string;
  // Source credentials. Optional: left blank, the server uses its own S3 keys
  // (only useful for the platform's internal MinIO). Required for any external
  // bucket. Used transiently for the import; never stored.
  accessKey?: string;
  secretKey?: string;
  region?: string;
}

export interface S3ImportJob {
  id: string;
  status: string;
  bucket: string;
  prefix?: string;
  archiveId?: string;
  imported?: number;
  bytes?: number;
}

// Cursor-based server-side pagination envelope.
export interface Page<T> {
  items: T[];
  nextCursor?: string | null;
}

// --- Admin console (customers, buckets, subscription plans) ----------------
// Mirrors services/archive-service/subscriptions.go. Admin-only; see
// docs/architecture/admin-console-concept.md.

export interface PlanVersion {
  id: string;
  version: number;
  writeSlaHours: number;
  minStoragePerArchiveBytes: number;
  purchasedStorageBytes: number;
  rateLimitWriteBytesPerDay: number;
  rateLimitReadBytesPerDay: number;
  verificationLevel: string;
  verificationFrequency: string;
  retrievalOptions: string[];
  priceWriteEurPerGb: number;
  priceReadFreeLifetimeCount: number;
  priceReadEurPerReadout: number;
  priceStorageEurPerGbYear: number;
  physicalPackaging: string;
  ttfbReadingHours: number;
  ttfbSiteId: string;
  eccProfile: string;
  storageLocationSiteId: string;
  minBillableObjectBytes: number;
  minBillableReadBytes: number;
  mediumRedundancyPercent: number;
  writeTriggers: string[];
  features: Record<string, unknown>;
  effectiveFrom: string;
}

export interface Plan {
  id: string;
  name: string;
  latestVersion: PlanVersion | null;
}

/** Request body for creating a plan or a new plan version — every field the
 * server assigns (id, version, effectiveFrom) is omitted. */
export type PlanVersionInput = Omit<PlanVersion, 'id' | 'version' | 'effectiveFrom'>;

/** Identifies one plan version across the whole catalog (not just the latest
 * per plan) — used to label a subscription's version without an N+1 lookup. */
export interface PlanVersionSummary {
  id: string;
  planId: string;
  planName: string;
  version: number;
}

export type SubscriptionStatus = 'active' | 'paused' | 'cancelled';

export interface Subscription {
  tenantId: string;
  planVersionId: string;
  status: SubscriptionStatus;
  overrides: Record<string, unknown>;
  currentPeriodStart: string;
  currentPeriodEnd: string;
}

export interface TenantRateLimit {
  hasLimit: boolean;
  usedBytes: number;
  limitBytes: number;
  percent: number;
  /** at or above the warning threshold (80%) */
  warn: boolean;
  overLimit: boolean;
  /** Operations actually refused by quota in the last 24h. */
  recentRefusals: number;
}

export interface TenantSummary {
  id: string;
  name: string;
  planName?: string;
  planVersion?: number;
  status?: SubscriptionStatus;
  archiveCount: number;
  bytes: number;
  rateLimit: TenantRateLimit;
}

// One resolved field of a tenant's effective limits: the winning value plus
// which layer produced it (concept doc §3.2) — never raw plan/override JSON.
export interface EffectiveLimitField {
  value: unknown;
  source: 'plan' | 'features' | 'override';
}

export interface EffectiveLimits {
  planId: string;
  planVersion: number;
  status: SubscriptionStatus;
  fields: Record<string, EffectiveLimitField>;
}

export interface TenantUsage {
  tenantId: string;
  periodStart: string;
  periodEnd: string;
  bytesWritten: number;
  bytesRead: number;
  rateLimitWriteBytesPerDay?: EffectiveLimitField;
  rateLimitReadBytesPerDay?: EffectiveLimitField;
}

export interface AdminSearchHit {
  kind: 'tenant' | 'archive' | 'object';
  tenantId: string;
  tenantName: string;
  archiveId?: string;
  archiveName?: string;
  path?: string;
}

// --- Admin console Phase 2 (migration preview, bulk actions, alerts,
// impersonation) — see docs/architecture/admin-console-concept.md §7.

export interface LimitChange {
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
}

export interface MigrationPreviewTenant {
  tenantId: string;
  tenantName: string;
  currentPlanVersion: number;
  changes: LimitChange[];
  droppedOverrides?: string[];
}

export interface MigrationPreview {
  toVersion: number;
  items: MigrationPreviewTenant[];
}

export type BulkAction = 'migrate' | 'pause' | 'cancel' | 'reactivate';

export interface BulkResult {
  tenantId: string;
  ok: boolean;
  message?: string;
}

export interface BreachAlert {
  tenantId: string;
  tenantName: string;
  metric: 'write';
  usedBytes: number;
  limitBytes: number;
  percent: number;
}

// --- Admin console Phase 3 (feature-flag matrix, verification schedule) ----

export interface FlagCell {
  value: unknown;
  source: 'plan' | 'features' | 'override';
}

export interface FlagMatrixRow {
  tenantId: string;
  tenantName: string;
  planName?: string;
  planVersion?: number;
  status?: SubscriptionStatus;
  cells: Record<string, FlagCell>;
  /** True if the subscription has ANY override, including limit/pricing
   *  fields that are not flag columns. */
  hasAnyOverride: boolean;
}

export interface FlagMatrix {
  columns: string[];
  items: FlagMatrixRow[];
}

export interface VerificationScheduleRow {
  tenantId: string;
  tenantName: string;
  frequency: string;
  intervalDays?: number;
  lastVerified?: string;
  dueAt?: string;
  overdue: boolean;
  /** The plan's verification frequency isn't a cadence the service can parse. */
  unschedulable: boolean;
}

// --- Admin console: buckets, users, dashboard ------------------------------

export interface AdminBucket {
  archiveId: string;
  name: string;
  tenantId: string;
  tenantName: string;
  retentionPolicy: RetentionPolicy;
  objects: number;
  bytes: number;
  createdAt: string;
  syncFreq: string;
  syncNext?: string;
  syncLast?: string;
  commitFreq: string;
  commitNext?: string;
  commitLast?: string;
  snapshots: number;
  lastSnapshotAt?: string;
  /** true when either sync or commit runs on a schedule. */
  scheduled: boolean;
}

/** An identity observed acting in the audit trail (accounts live in Keycloak). */
export interface AdminUser {
  actor: string;
  tenantId: string;
  tenantName: string;
  actions: number;
  firstSeen: string;
  lastSeen: string;
  lastAction: string;
}

export type UpcomingKind = 'sync' | 'commit' | 'verification';

export interface UpcomingEvent {
  at: string;
  kind: UpcomingKind;
  tenantId: string;
  tenantName: string;
  archiveId?: string;
  bucket?: string;
  overdue: boolean;
}

export interface AdminDashboard {
  totals: {
    customers: number;
    buckets: number;
    scheduledBuckets: number;
    objects: number;
    bytes: number;
    snapshots: number;
  };
  subscriptionsByPlan: Record<string, number>;
  subscriptionsByStatus: Record<string, number>;
  upcoming: UpcomingEvent[];
  recentActivity: { at: string; actor: string; action: string; resource: string; result: string }[];
}

/** Answer to "may this archive be committed right now?" (advisory; the server
 *  re-checks on commit). */
export interface CommitPreflight {
  allowed: boolean;
  hasLimit: boolean;
  estimatedBytes: number;
  usedBytes: number;
  limitBytes: number;
  remainingBytes: number;
  /** What this commit is quoted at; 0 when the tenant has no priced plan. */
  estimatedCostEur: number;
  currency: string;
}

/** The caller's own effective price list (negotiated overrides included), so
 *  the portal can quote any chargeable action before it happens. */
export interface StorageQuota {
  hasQuota: boolean;
  purchasedBytes: number;
  /** Only data actually burned to glass counts as used. */
  burnedBytes: number;
  remainingBytes: number;
  percent: number;
  snapshots: number;
  /** Uploaded but not yet burned — staged, not consumed. */
  stagedBytes: number;
  planName?: string;
}

export interface TenantPricing {
  hasPlan: boolean;
  planName?: string;
  planVersion?: number;
  currency: string;
  writeEurPerGb: number;
  storageEurPerGbYear: number;
  readEurPerReadout: number;
  freeReadoutsLifetime: number;
  /** Files smaller than this are still charged at this size. */
  minBillableObjectBytes: number;
}

/** One burn (sealed DMD image) as the admin console lists it. */
export interface AdminSnapshot {
  snapshotId: string;
  archiveId: string;
  archiveName: string;
  tenantId: string;
  tenantName: string;
  seq: number;
  trigger: string;
  objects: number;
  bytes: number;
  dmdSize: number;
  /** Digest of the sealed image — the evidence that this burn produced it. */
  dmdSha256: string;
  createdBy: string;
  createdAt: string;
}

/** A customer's request to seal an archive onto glass, awaiting a decision. */
export interface BurnRequest {
  id: string;
  archiveId: string;
  archiveName?: string;
  tenantId: string;
  tenantName?: string;
  /** requested | approved | building | completed | rejected | failed */
  status: string;
  requestedBy: string;
  requestedAt: string;
  /** The quote frozen at request time — what the customer agreed to. */
  estBytes: number;
  estObjects: number;
  estCostEur: number;
  decidedBy?: string;
  decidedAt?: string;
  note?: string;
  snapshotId?: string;
  error?: string;
  /** Still awaiting an operator decision. */
  pending: boolean;
}
